import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  Renderer2,
  signal,
  viewChild,
  viewChildren
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivationEnd, NavigationEnd, RouteConfigLoadEnd, Router } from '@angular/router';
import { Observable } from 'rxjs';

import { RouterLinkWithHrefDirective } from '@amfam/shared/ui/ds-router-link/lib/router-link.directive';
import { DsButtonComponent } from '@amfam/ui-kit/lib/ds-button/components/ds-button/ds-button.component';

import { HeaderNavRect } from '../../../models/header-data.model';
import { LinkClickEvent, NavLinkTreeObj, SublinkClickEvent } from '../../../models/links-model';
import { HeaderWrapperService } from '../../../services/header-wrapper.service';

@Component({
  selector: 'ds-header-navigation-item',
  templateUrl: './ds-header-navigation-item.component.html',
  styleUrls: ['../shared/ds-header-navigation-links.scss'],
  host: {
    '(window:resize)': 'resize()',
    '(keyup.esc)': 'onEsc()'
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLinkWithHrefDirective, DsButtonComponent]
})
export class DsHeaderNavigationItemComponent implements AfterViewInit {
  private router = inject(Router);
  private headerWrapperService = inject(HeaderWrapperService);
  private renderer = inject(Renderer2);
  private destroyRef = inject(DestroyRef);

  // An event stream, not template state: each emission re-runs the active-link check.
  readonly loaded$ = input<Observable<boolean>>(this.headerWrapperService.loaded$);
  readonly link = input<NavLinkTreeObj>();
  readonly showSub = signal('hide');
  readonly headerNavRect = input.required<HeaderNavRect | undefined | null>();
  readonly subOpenEvent = output<DsHeaderNavigationItemComponent>();
  readonly linkClickEvent = output<LinkClickEvent>();
  readonly sublinkClickEvent = output<SublinkClickEvent>();
  readonly linkContainer = viewChild.required<ElementRef>('linkContainer');
  readonly linkElem = viewChild.required<ElementRef>('linkElem');
  readonly subLinkElem = viewChild<ElementRef>('sublinkElem');
  readonly subLinkElemListItems = viewChildren<ElementRef>('subLinkElemList');
  private linkActive: ElementRef | undefined;
  private readonly routerStateUrl = toSignal(this.headerWrapperService.routerStateUrl$, {
    initialValue: ''
  });
  readonly submenuExpanded = signal('false');
  readonly submenuHasPopup = computed(() => this.link()?.subLinkArray !== undefined);

  resize() {
    // Re-run sub menu centering if window resize occurs
    this.getElementCenter(this.linkElem(), this.subLinkElem());
  }

  // ESC has to close menu for WCAG 2.0
  onEsc() {
    this.showSub.set('hide');
    this.submenuExpanded.set('false');
  }

  ngAfterViewInit() {
    this.headerWrapperService.linkActive$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((linkActive: ElementRef | null) => {
        if (linkActive?.nativeElement) {
          this.linkActive = linkActive;
        } else {
          // if no active link, set default - should be first
          this.headerWrapperService.setLinkActive(this.linkElem());
        }
      });
    window.setTimeout(() => {
      // Set sublink active if it matches the active sublink in the service
      this.headerWrapperService.sublinkActive$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(sublinkActive => {
          this.subLinkElemListItems().forEach(subLinkElemListItem => {
            if (subLinkElemListItem.nativeElement.classList.contains('active')) {
              subLinkElemListItem.nativeElement.classList.remove('active');
            } else if (sublinkActive === subLinkElemListItem.nativeElement) {
              subLinkElemListItem.nativeElement.classList.add('active');
            }
          });
        });

      // Set link active if it matches the router.url
      this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
        this.determineLinkStatus(this.linkElem(), this.subLinkElem(), event);
      });

      // Set link active again as things load
      this.loaded$()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.determineLinkStatus(this.linkElem(), this.subLinkElem(), this.headerNavRect());
        });
    }, 1);
  }

  linkClick(link: NavLinkTreeObj | undefined, event: Event) {
    this.linkClickEvent.emit({ link, event });
    this.showSub.set('show');
    if (this.submenuHasPopup()) {
      this.submenuExpanded.set('true');
    }

    this.setLinkActive(this.linkElem());
  }

  sublinkClick(sublink: NavLinkTreeObj | undefined, event: Event) {
    const sublinkElemItem = event.currentTarget as HTMLElement;
    this.sublinkClickEvent.emit({ sublink, event });
    this.setLinkActive(this.linkElem(), 'active');
    const sublinkElem = sublinkElemItem.parentElement;
    if (sublinkElem) this.headerWrapperService.setSublinkActive(sublinkElem);
  }

  subOpen(focusedElem?: EventTarget | null) {
    // TODO: [DRE-1307] jjc - rework submenu to treat tabs and hover independently
    // Technically, we should treat tabs differently than hover
    // See: https://www.w3.org/WAI/tutorials/menus/flyout/#keyboard-users
    if (this.showSub() !== 'show') {
      // Hide other nav items / sub menus
      this.subOpenEvent.emit(this);
      // Show this sub menu
      this.showSub.set('show');
    }

    if (focusedElem !== undefined) {
      if (this.linkContainer().nativeElement.contains(focusedElem)) {
        if (this.submenuHasPopup() && this.submenuExpanded() === 'false') {
          this.submenuExpanded.set('true');
        }
      }
    } else {
      if (this.submenuHasPopup() && this.submenuExpanded() === 'false') {
        this.submenuExpanded.set('true');
      }
    }
    this.setLinkFocus(this.linkElem());
  }

  subClose(focusedElem?: EventTarget | null) {
    if (this.showSub() !== 'hide') {
      // Hide this sub menu
      this.showSub.set('hide');
    }

    if (focusedElem !== undefined) {
      if (this.linkContainer().nativeElement.contains(focusedElem)) {
        if (this.submenuHasPopup() && this.submenuExpanded() === 'false') {
          this.submenuExpanded.set('true');
        }
      }
    } else {
      if (this.submenuExpanded() === 'true') {
        this.submenuExpanded.set('false');
      }
    }
    this.setLinkBlur(this.linkElem());
  }

  private determineLinkStatus(
    linkElem: ElementRef,
    subLinkElem: ElementRef | undefined,
    navEvent?: unknown
  ) {
    if (linkElem) {
      if (subLinkElem) {
        this.getElementCenter(linkElem, subLinkElem);
      }
      // If the current linkElem is in the store as the linkActive Elem
      // Or the current routerStateUrl is the same as the linkRoute prop for this link
      // Then set this link as active
      const link = this.link();
      if (linkElem === this.linkActive || this.routerStateUrl() === link?.linkRoute) {
        this.setLinkActive(linkElem, 'active');
        // Else if the current linkElem is not in the service as the linkActive Elem
        // Move on...
      } else if (linkElem !== this.linkActive) {
        // If we have a navEvent other than NavigationEnd or RouteConfigLoadEnd
        // Bail, no need to try matching on them
        if (navEvent) {
          const navEventAggregate =
            navEvent instanceof NavigationEnd ||
            navEvent instanceof RouteConfigLoadEnd ||
            navEvent instanceof ActivationEnd;
          if (!navEventAggregate) {
            return false;
          } else {
            if (navEventAggregate) {
              this.centerSubNavOnNavEnd();
            }
            // If the router.url is the same as the linkRoute prop of this link
            // Or the routerStateUrl is the same as the linkRoute prop of this link
            // Or if there's a fuzzy match between the routerStateUrl is the same as the linkRoute prop of this link
            // Then set link as active
            if (
              link?.linkRoute === this.router.url ||
              this.routerStateUrl() === link?.linkRoute ||
              this.fuzzyUrlMatch(this.routerStateUrl(), link?.linkRoute ?? '')
            ) {
              this.setLinkActive(linkElem, 'active');
            } else {
              if (linkElem.nativeElement.classList.contains('active')) {
                this.renderer.removeClass(linkElem.nativeElement, 'active');
              }
              if (linkElem.nativeElement.hasAttribute('aria-current')) {
                linkElem.nativeElement.removeAttribute('aria-current');
              }
            }
          }
        }
      }
    }
  }

  private getElementCenter(linkElem: ElementRef, subLinkElem: ElementRef | undefined) {
    this.headerWrapperService.headerNavRect$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(headerNavRect => {
        this.headerWrapperService.centerSubNav(linkElem, subLinkElem, headerNavRect);
      });
  }

  private centerSubNavOnNavEnd() {
    // Centers the sub menu under the nav item on navigation end.
    if (this.subLinkElem()) {
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.getElementCenter(this.linkElem(), this.subLinkElem());
        }
      });
    }
  }

  private setLinkActive(linkElem: ElementRef, className = '') {
    if (className.length) {
      this.renderer.addClass(linkElem.nativeElement, className);
    }
    linkElem.nativeElement.setAttribute('aria-current', 'page');
    this.headerWrapperService.setLinkActive(linkElem);
  }

  private setLinkFocus(linkElem: ElementRef) {
    this.renderer.addClass(linkElem.nativeElement, 'focus');
    this.headerWrapperService.setLinkFocus(linkElem);
  }

  private setLinkBlur(linkElem: ElementRef) {
    this.renderer.removeClass(linkElem.nativeElement, 'focus');
    this.headerWrapperService.setLinkFocus(null);
  }

  private fuzzyUrlMatch(routerUrl: string, linkRoute: string): boolean | undefined {
    if (typeof routerUrl === 'string' && typeof linkRoute === 'string') {
      // Match starting with / to the next / or a ?. Example here: https://regexr.com/5k23d
      const regex = /(?:\/)(.*?)(?=[?/])/g;
      const resultMatchGroup = routerUrl.match(regex);
      if (resultMatchGroup) {
        // Destructure to get first array property
        const [desiredRes] = resultMatchGroup;
        return desiredRes === linkRoute;
      }
    }
  }
}
